import { COLOR } from './shared/shared';
import React, { ReactElement, useState, useEffect } from 'react';

const gButtonTimeout = 125;

export interface ButtonProps {
	pAction(): void;
	pClass?: string;
	className?: string;
	pClickRed?: boolean;
	pDisabled?: boolean;
	pNormalColorOverride?: COLOR;
	pClickColorOverride?: COLOR;
	children: ReactElement | string;
	title?: string;
}

interface BackgroundStyle {
	backgroundColor: COLOR;
}

export interface TextButtonProps {
	pSpanClass: string;
	pText: string;
	pDefaultColor: COLOR;
	pClickColor: COLOR;
	pAction(): void;
}

interface ColorStyle {
	color: COLOR;
}

export function TextButton({
	pSpanClass,
	pText,
	pDefaultColor,
	pClickColor,
	pAction,
}: TextButtonProps) {
	const [lStyle, lSetStyle] = useState<ColorStyle>({ color: pDefaultColor }),
		[lTimeout, lSetTimeout] = useState<number | null>(null),
		lSetColor = function (pColor: COLOR) {
			const lCp = { ...lStyle };
			lCp.color = pColor;
			lSetStyle(lCp);
		},
		lOnClick = function () {
			lSetColor(pClickColor);
			if (null !== lTimeout) window.clearTimeout(lTimeout);
			const lTimeoutId = window.setTimeout(function () {
				lSetColor(pDefaultColor);
				lSetTimeout(null);
				pAction();
			}, gButtonTimeout);
			lSetTimeout(lTimeoutId);
		};
	useEffect(
		function () {
			lSetColor(lTimeout !== null ? pClickColor : pDefaultColor);
		},
		[pDefaultColor, pClickColor]
	);
	useEffect(function () {
		return function () {
			/* cleanup */ if (lTimeout !== null) window.clearTimeout(lTimeout);
		};
	}, []);
	return (
		<span className={pSpanClass} onClick={lOnClick} style={lStyle}>
			{pText}
		</span>
	);
}

export default function Button({
	pClass,
	pAction,
	children,
	pClickRed,
	pDisabled,
	className,
	pNormalColorOverride,
	pClickColorOverride,
	title,
}: ButtonProps) {
	const lGetNormalColor = function () {
			if (pNormalColorOverride && pNormalColorOverride.length > 0)
				return pNormalColorOverride;
			return pDisabled ? COLOR.DARK_GREY : COLOR.MEDIUM_BLUE;
		},
		[lStyle, lSetStyle] = useState<BackgroundStyle>({
			backgroundColor: lGetNormalColor(),
		}),
		[lTimeout, lSetTimeout] = useState<number | null>(null),
		lSetColor = function (pColor: COLOR) {
			const lCp = { ...lStyle };
			lCp.backgroundColor = pColor;
			lSetStyle(lCp);
		},
		lSetNormalColor = function () {
			lSetColor(lGetNormalColor());
		},
		lSetClickColor = function () {
			lSetColor(
				pClickRed
					? COLOR.RED
					: pClickColorOverride
					? pClickColorOverride
					: COLOR.GREEN
			);
		},
		lOnClick = function () {
			if (pDisabled) return;
			lSetClickColor();
			if (lTimeout !== null) window.clearTimeout(lTimeout);
			const lTimeOutId = window.setTimeout(function () {
				lSetNormalColor();
				lSetTimeout(null);
				pAction();
			}, gButtonTimeout);
			lSetTimeout(lTimeOutId);
		};
	useEffect(
		function () {
			if (lTimeout !== null) return;
			lSetNormalColor();
		},
		[pDisabled, pNormalColorOverride]
	);
	useEffect(function () {
		return function () {
			/* cleanup */ if (lTimeout !== null) window.clearTimeout(lTimeout);
		};
	}, []);
	return (
		<button
			className={pClass || className || ''}
			style={lStyle}
			onClick={lOnClick}
			title={title}
		>
			{children}
		</button>
	);
}
